/**
 * Knowledge Base — Layer 2 of the AI Router.
 *
 * A small, curated set of app-identity / policy FAQ answers, checked BEFORE
 * the calculator engine runs. This is intentionally narrow: it only matches
 * questions ABOUT the app itself (who made it, is it free, does it store
 * data...) so it can never intercept an actual calculation query or a
 * smallTalk greeting that the calculator engine (aiEngine.js) already
 * handles on its own.
 *
 * Purely additive/new — does not read or modify anything in aiEngine.js.
 * Add more entries any time; each is checked independently and the first
 * match wins.
 */

const ENTRIES = [
  {
    pattern: /\b(who (made|built|created)|who is behind)\b.*\b(this|smartcalc|you|app)\b/i,
    answer:
      "smartcalcAi is a rule-based natural-language calculator app. It runs entirely in your browser — there's no company or team behind the scenes, just the calculation engine answering your questions.",
  },
  {
    pattern: /\bwhat is smartcalc\s*ai\b|\babout (this|the) app\b/i,
    answer:
      "smartcalcAi lets you type a calculation in plain English, Hindi, or Hinglish — like \"EMI for 5 lakh at 8.5% for 5 years\" — and get the answer with the formula and step-by-step math, no menus needed.",
    suggestions: ["what can you calculate", "15% of 2500"],
  },
  {
    pattern: /\bis (this|it|smartcalc\s*ai) free\b|\bdoes it cost\b|\bpricing\b/i,
    answer: "Yes, smartcalcAi is completely free to use.",
  },
  {
    pattern: /\bdo you (store|save|keep) my data\b|\bis my data (safe|private)\b|\bprivacy\b/i,
    answer:
      "Your calculations run locally in your browser. History and theme preference are saved only in your browser's local storage — nothing is sent to a server, except an optional live lookup for currency conversion rates.",
  },
  {
    pattern: /\bare you (a real )?(ai|human)\b|\bwhat model are you\b/i,
    answer:
      "I'm smartcalcAi's built-in calculation engine — a rule-based system, not a general AI model, for anything I can already calculate. For open-ended questions I can also check in with an AI provider if one is configured.",
  },
];

/**
 * Returns { answer, suggestions? } on a match, or null.
 */
export function lookupKnowledgeBase(rawQuery) {
  const q = String(rawQuery || "").trim();
  if (!q) return null;
  for (const entry of ENTRIES) {
    if (entry.pattern.test(q)) {
      return { answer: entry.answer, suggestions: entry.suggestions || [] };
    }
  }
  return null;
}

export default ENTRIES;
